import React, { useMemo } from 'react';
import './DashboardOverview.css';

/* -------- Visualization primitives (pure SVG, no chart lib) -------- */

const Sparkline = ({ values = [], color = '#e0a3ff', width = 100, height = 28 }) => {
  if (!values.length) {
    return <svg width={width} height={height} className="dov-spark" />;
  }
  const max = Math.max(...values, 1);
  const min = 0;
  const stepX = width / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / (max - min || 1)) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${points.join(' L ')}`;
  const area = `${path} L ${width},${height} L 0,${height} Z`;
  const last = points[points.length - 1].split(',');
  return (
    <svg width={width} height={height} className="dov-spark" viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#spark-${color})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
};

const BarChart = ({ buckets = [], labels = [], height = 200 }) => {
  const max = Math.max(...buckets, 1);
  const barW = 100 / Math.max(buckets.length, 1);
  const peakIdx = buckets.indexOf(Math.max(...buckets));
  return (
    <div className="dov-barchart" style={{ height }}>
      <div className="dov-barchart-grid" aria-hidden="true">
        {[0, 1, 2, 3].map(i => <div key={i} className="dov-grid-line" style={{ bottom: `${(i / 3) * 100}%` }} />)}
      </div>
      <div className="dov-bars">
        {buckets.map((v, i) => {
          const h = max ? (v / max) * 100 : 0;
          return (
            <div
              key={i}
              className={`dov-bar-col ${i === peakIdx && v > 0 ? 'is-peak' : ''}`}
              style={{ width: `${barW}%` }}
              title={`${labels[i] || ''}: ${v}`}
            >
              <div className="dov-bar" style={{ height: `${h}%` }}>
                {v > 0 && <span className="dov-bar-value">{v}</span>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="dov-bar-labels">
        {labels.map((l, i) => (
          <span key={i} style={{ width: `${barW}%` }}>{l}</span>
        ))}
      </div>
    </div>
  );
};

const Donut = ({ segments = [], size = 220, thickness = 28, centerLabel, centerValue }) => {
  const total = segments.reduce((s, x) => s + x.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  if (total === 0) {
    return (
      <div className="dov-donut" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={thickness} />
        </svg>
        <div className="dov-donut-center">
          <div className="dov-donut-value">0</div>
          <div className="dov-donut-label">no activity yet</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dov-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={thickness} />
        {segments.map((seg, i) => {
          if (seg.value === 0) return null;
          const dash = (seg.value / total) * circumference;
          const gap = circumference - dash;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className="dov-donut-center">
        <div className="dov-donut-value">{centerValue ?? total}</div>
        <div className="dov-donut-label">{centerLabel ?? 'total'}</div>
      </div>
    </div>
  );
};

/* -------- Helpers to derive chart data from raw activity -------- */

const TOOL_PALETTE = {
  'Interview Assistant':  '#e0a3ff',
  'Mock Interview':       '#ff69b4',
  'Resume Builder':       '#00ffff',
  'Cover Letter':         '#9370db',
  'Interview Questions':  '#ffd166',
  'AI Quiz':              '#ffd166',
  'LinkedIn Optimizer':   '#7ee8a3',
  'Auto Apply':           '#ff8c69',
  'Portfolio':            '#a78bfa',
  'Certification':        '#fbbf77',
  'Other':                '#888899',
};

const TOOL_ICON = {
  'Interview Assistant': '🏆',
  'Mock Interview': '🔮',
  'Resume Builder': '📄',
  'Cover Letter': '🛡️',
  'Interview Questions': '💎',
  'AI Quiz': '🎯',
  'LinkedIn Optimizer': '📈',
  'Auto Apply': '⚡',
  'Portfolio': '🌐',
  'Certification': '🎓',
};

/** Bucket activities into the last `weeks` weeks. Returns { values, labels }. */
const bucketByWeek = (activities, weeks = 12) => {
  const now = new Date();
  // anchor to monday of this week
  const startOfWeek = (d) => {
    const x = new Date(d);
    const dow = (x.getDay() + 6) % 7; // mon = 0
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - dow);
    return x;
  };
  const thisMonday = startOfWeek(now);
  const buckets = new Array(weeks).fill(0);
  const labels = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(thisMonday);
    d.setDate(d.getDate() - i * 7);
    labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  activities.forEach(a => {
    const t = new Date(a.created_at);
    if (isNaN(t)) return;
    const monOfThat = startOfWeek(t);
    const diffWeeks = Math.floor((thisMonday - monOfThat) / (7 * 24 * 60 * 60 * 1000));
    const idx = weeks - 1 - diffWeeks;
    if (idx >= 0 && idx < weeks) buckets[idx] += 1;
  });
  return { values: buckets, labels };
};

/** Bucket activities by day of week (Mon..Sun) for last 30 days. */
const bucketByDayOfWeek = (activities, days = 30) => {
  const buckets = [0, 0, 0, 0, 0, 0, 0]; // Mon..Sun
  const now = new Date();
  const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - days);
  activities.forEach(a => {
    const t = new Date(a.created_at);
    if (isNaN(t) || t < cutoff) return;
    const dow = (t.getDay() + 6) % 7;
    buckets[dow] += 1;
  });
  return buckets;
};

/** Per-tool sparkline values: events per week for last 8 weeks. */
const sparklineFor = (activities, type, weeks = 8) => {
  const filtered = activities.filter(a => a.type === type);
  return bucketByWeek(filtered, weeks).values;
};

/** Streak: consecutive days (ending today) with at least one activity. */
const computeStreak = (activities) => {
  if (!activities.length) return 0;
  const days = new Set();
  activities.forEach(a => {
    const t = new Date(a.created_at);
    if (isNaN(t)) return;
    const k = t.toISOString().slice(0, 10);
    days.add(k);
  });
  let streak = 0;
  const cur = new Date();
  for (;;) {
    const k = cur.toISOString().slice(0, 10);
    if (days.has(k)) {
      streak++;
      cur.setDate(cur.getDate() - 1);
    } else {
      // allow missing today, count from yesterday
      if (streak === 0) {
        cur.setDate(cur.getDate() - 1);
        const k2 = cur.toISOString().slice(0, 10);
        if (!days.has(k2)) break;
        continue;
      }
      break;
    }
  }
  return streak;
};

/* -------- Card components -------- */

const KPI = ({ label, value, sub, icon, color = '#e0a3ff', spark }) => (
  <div className="dov-kpi">
    <div className="dov-kpi-row">
      <span className="dov-kpi-label">{label}</span>
      <span className="dov-kpi-icon" style={{ background: `linear-gradient(135deg, ${color}33, ${color}11)`, borderColor: `${color}55` }}>
        {icon}
      </span>
    </div>
    <div className="dov-kpi-value">{value}</div>
    {sub && <div className="dov-kpi-sub">{sub}</div>}
    {spark && <div className="dov-kpi-spark"><Sparkline values={spark} color={color} width={120} height={28} /></div>}
  </div>
);

const formatActivityDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);
  if (diffMin < 60) return `${Math.max(1, diffMin)}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* -------- Main component -------- */

const DashboardOverview = ({
  user,
  stats = {},
  recentActivities = [],
  autoApplySessions = [],
  allAppliedJobs = [],
  onNavigate,
}) => {
  const totalSessions =
    (stats.totalInterviews || 0) +
    (stats.resumesBuilt || 0) +
    (stats.coverLetters || 0) +
    (stats.interviewQuestions || 0) +
    (stats.mockInterviews || 0);

  const weekly = useMemo(() => bucketByWeek(recentActivities, 12), [recentActivities]);
  const dayOfWeek = useMemo(() => bucketByDayOfWeek(recentActivities, 30), [recentActivities]);
  const streak = useMemo(() => computeStreak(recentActivities), [recentActivities]);
  const last4 = useMemo(() => recentActivities.slice(0, 6), [recentActivities]);

  const distribution = useMemo(() => ([
    { label: 'Interview Assistant',  value: stats.totalInterviews || 0,    color: TOOL_PALETTE['Interview Assistant'] },
    { label: 'Mock Interview',       value: stats.mockInterviews || 0,     color: TOOL_PALETTE['Mock Interview'] },
    { label: 'Resume Builder',       value: stats.resumesBuilt || 0,       color: TOOL_PALETTE['Resume Builder'] },
    { label: 'Cover Letter',         value: stats.coverLetters || 0,       color: TOOL_PALETTE['Cover Letter'] },
    { label: 'Interview Questions',  value: stats.interviewQuestions || 0, color: TOOL_PALETTE['Interview Questions'] },
  ]), [stats]);

  const topTool = useMemo(() => {
    const sorted = [...distribution].filter(s => s.value > 0).sort((a, b) => b.value - a.value);
    return sorted[0] || null;
  }, [distribution]);

  const recentSpark = (label) => sparklineFor(recentActivities, label, 8);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return 'Burning midnight oil';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Good night';
  })();

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div className="dov-root">
      {/* ── greeting strip ───────────────────────────── */}
      <div className="dov-greeting">
        <div>
          <h1 className="dov-greeting-title">{greeting}, {firstName}.</h1>
          <p className="dov-greeting-sub">
            {totalSessions === 0
              ? 'Pick a tool below to log your first session.'
              : `You've completed ${totalSessions} sessions across ${distribution.filter(d => d.value > 0).length} tools.`}
          </p>
        </div>
        <div className="dov-streak">
          <span className="dov-streak-flame" aria-hidden="true">🔥</span>
          <div>
            <div className="dov-streak-value">{streak}-day{streak === 1 ? '' : 's'}</div>
            <div className="dov-streak-label">activity streak</div>
          </div>
        </div>
      </div>

      {/* ── KPI strip ───────────────────────────────── */}
      <div className="dov-kpi-grid">
        <KPI
          label="Total sessions"
          value={totalSessions}
          sub={`${recentActivities.length} in the last 30 days`}
          icon="✨"
          color="#e0a3ff"
          spark={weekly.values}
        />
        <KPI
          label="Jobs applied"
          value={allAppliedJobs.length}
          sub={`${autoApplySessions.length} auto-apply runs`}
          icon="⚡"
          color="#00ffff"
          spark={recentSpark('Auto Apply')}
        />
        <KPI
          label="Resumes built"
          value={stats.resumesBuilt || 0}
          sub={`${stats.coverLetters || 0} cover letters`}
          icon="📄"
          color="#ff69b4"
          spark={recentSpark('Resume Builder')}
        />
        <KPI
          label="Top tool"
          value={topTool ? topTool.label.split(' ')[0] : '—'}
          sub={topTool ? `${topTool.value} sessions` : 'no data yet'}
          icon={topTool ? (TOOL_ICON[topTool.label] || '🎯') : '🎯'}
          color={topTool ? topTool.color : '#9370db'}
          spark={topTool ? recentSpark(topTool.label) : []}
        />
      </div>

      {/* ── activity bar chart + donut ──────────────── */}
      <div className="dov-charts">
        <div className="dov-card dov-chart-bars">
          <div className="dov-card-head">
            <div>
              <h3>Activity over time</h3>
              <p className="dov-card-sub">Last 12 weeks · {weekly.values.reduce((a, b) => a + b, 0)} events</p>
            </div>
            <div className="dov-legend">
              <span className="dov-legend-dot" style={{ background: '#e0a3ff' }} />
              <span>Sessions</span>
            </div>
          </div>
          <BarChart
            buckets={weekly.values}
            labels={weekly.labels.map((l, i) => i % 2 === 0 ? l.split(' ')[0] : '')}
            height={220}
          />
          <div className="dov-dow">
            <div className="dov-dow-label">Active days (last 30d)</div>
            <div className="dov-dow-row">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((lbl, i) => {
                const v = dayOfWeek[i];
                const max = Math.max(...dayOfWeek, 1);
                const intensity = max ? v / max : 0;
                return (
                  <div key={i} className="dov-dow-cell" style={{
                    background: `rgba(224, 163, 255, ${0.06 + intensity * 0.55})`,
                    borderColor: `rgba(224, 163, 255, ${0.10 + intensity * 0.50})`,
                  }} title={`${lbl}: ${v} events`}>
                    <span>{lbl}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="dov-card dov-chart-donut">
          <div className="dov-card-head">
            <div>
              <h3>Tool distribution</h3>
              <p className="dov-card-sub">Where your sessions live</p>
            </div>
          </div>
          <Donut
            segments={distribution}
            size={210}
            thickness={26}
            centerValue={totalSessions}
            centerLabel="sessions"
          />
          <div className="dov-donut-legend">
            {distribution.map(s => (
              <div key={s.label} className="dov-legend-item">
                <span className="dov-legend-dot" style={{ background: s.color }} />
                <span className="dov-legend-name">{s.label}</span>
                <span className="dov-legend-val">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── recent feed + tool sparks ───────────────── */}
      <div className="dov-bottom">
        <div className="dov-card dov-recent">
          <div className="dov-card-head">
            <div>
              <h3>Recent activity</h3>
              <p className="dov-card-sub">{recentActivities.length === 0 ? 'No activity yet' : `${recentActivities.length} events tracked`}</p>
            </div>
          </div>
          {last4.length === 0 ? (
            <div className="dov-empty">
              <div className="dov-empty-icon">📋</div>
              <p>No activity yet — your sessions will show up here.</p>
            </div>
          ) : (
            <ul className="dov-recent-list">
              {last4.map((a, i) => {
                const color = TOOL_PALETTE[a.type] || TOOL_PALETTE.Other;
                return (
                  <li key={i} className="dov-recent-item">
                    <span className="dov-recent-icon" style={{
                      background: `linear-gradient(135deg, ${color}33, ${color}11)`,
                      borderColor: `${color}55`,
                    }}>
                      {TOOL_ICON[a.type] || '📌'}
                    </span>
                    <div className="dov-recent-body">
                      <div className="dov-recent-title">{a.title}</div>
                      <div className="dov-recent-meta">
                        <span style={{ color }}>{a.type}</span>
                        <span className="dov-recent-dot">·</span>
                        <span>{formatActivityDate(a.created_at)}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="dov-card dov-trends">
          <div className="dov-card-head">
            <div>
              <h3>Tool trends</h3>
              <p className="dov-card-sub">Last 8 weeks per tool</p>
            </div>
          </div>
          <div className="dov-trend-list">
            {distribution.map(seg => {
              const sp = recentSpark(seg.label);
              const recent = sp.reduce((a, b) => a + b, 0);
              return (
                <div
                  key={seg.label}
                  className="dov-trend-row"
                  onClick={() => onNavigate?.(seg.label)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="dov-trend-icon" style={{
                    background: `linear-gradient(135deg, ${seg.color}33, ${seg.color}11)`,
                    borderColor: `${seg.color}55`,
                  }}>{TOOL_ICON[seg.label] || '📌'}</span>
                  <div className="dov-trend-meta">
                    <div className="dov-trend-name">{seg.label}</div>
                    <div className="dov-trend-sub">{seg.value} total · {recent} recent</div>
                  </div>
                  <Sparkline values={sp} color={seg.color} width={88} height={26} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
